import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ABILITY_INFO, ALL_PIECES, PRESETS, applyAction, createGame, defaultRules, legalActions,
  type Action, type GameEvent, type GameState, type Ruleset,
} from '@ic/engine';
import { pieceMoveText } from '../lib/pieceText';
import { activeRuleset, settings, useStore } from '../lib/store';
import { Board } from './Board';
import { Icon } from './Icons';
import { PieceIcon } from './Pieces';

const ROSTER = ['dragon', 'bomb', 'frog', 'elephant', 'cook', 'cockroach', 'gardener', 'miner'];
const HOME = 'd4';

/** A 7×7 sandbox: the piece in the middle, a few black targets that never move back. */
function sandbox(id: string): GameState {
  const rs: Ruleset = {
    schema: 1, id: `playground-${id}`, name: ALL_PIECES[id].name,
    board: { width: 7, height: 7, voids: [], walls: [] },
    pieces: { king: ALL_PIECES.king, pawn: ALL_PIECES.pawn, knight: ALL_PIECES.knight, [id]: ALL_PIECES[id] },
    setup: [
      { sq: HOME, type: id, color: 'w' }, { sq: 'a1', type: 'king', color: 'w' },
      { sq: 'g7', type: 'king', color: 'b' }, { sq: 'f6', type: 'knight', color: 'b' },
      { sq: 'd6', type: 'pawn', color: 'b' }, { sq: 'e5', type: 'pawn', color: 'b' },
      { sq: 'b4', type: 'pawn', color: 'b' }, { sq: 'f2', type: 'pawn', color: 'b' },
    ],
    rules: { ...defaultRules(), castling: false, promotion: { enabled: false, zone: 1, choices: [] } },
  };
  return createGame(rs);
}

/** The smallest preset that has this piece on the board; Illegal Chess only as a last resort. */
function presetFor(id: string): Ruleset {
  const has = PRESETS.filter((p) => p.setup.some((s) => s.type === id));
  has.sort((a, b) => Number(a.id === 'illegal') - Number(b.id === 'illegal') || a.board.width * a.board.height - b.board.width * b.board.height);
  return has[0] ?? PRESETS.find((p) => p.id === 'illegal')!;
}

/**
 * Home page: pick one of the new pieces and play with it on a small board, abilities included.
 * Only White moves; Black's turn is skipped so the visitor can keep experimenting.
 */
export function PiecePlayground() {
  const nav = useNavigate();
  const set = useStore(settings);
  const [id, setId] = useState('dragon');
  const [game, setGame] = useState(() => sandbox('dragon'));
  const [last, setLast] = useState<{ action: Action; events: GameEvent[]; key: number } | null>(null);
  const [tried, setTried] = useState(0);
  const legal = useMemo(() => (game.result ? [] : legalActions(game)), [game]);
  const def = ALL_PIECES[id];
  const preset = presetFor(id);

  const pick = (next: string) => { setId(next); setGame(sandbox(next)); setLast(null); setTried(0); };
  const reset = () => { setGame(sandbox(id)); setLast(null); setTried(0); };
  const onAction = (a: Action) => {
    const r = applyAction(game, a);
    const next = r.state;
    // Draws (repetition, stalemate) mean nothing here; only taking the king ends the sandbox.
    if (next.result && next.result.winner !== 'w') next.result = null;
    if (!next.result) next.turn = 'w';
    setGame(next);
    setLast((l) => ({ action: a, events: r.events, key: (l?.key ?? 0) + 1 }));
    setTried((n) => n + 1);
  };

  // Until the first move, the piece pulses so the visitor knows where to start.
  const marks: Record<number, string> = {};
  if (tried === 0) {
    const sq = game.board.findIndex((p) => p?.type === id);
    if (sq >= 0) marks[sq] = 'pg-pulse';
  }

  const play = () => {
    activeRuleset.set(JSON.parse(JSON.stringify(preset)));
    nav('/play/bot?level=2&color=w&tc=0+0');
  };

  return (
    <section className="playground" aria-labelledby="pg-title">
      <div className="pg-head">
        <span className="pg-kicker">Try it, no sign-up</span>
        <h2 id="pg-title">Meet the illegal pieces</h2>
        <div className="pg-roster" role="tablist" aria-label="Pieces">
          {ROSTER.map((p) => (
            <button key={p} type="button" role="tab" aria-selected={p === id} className={`pg-chip ${p === id ? 'on' : ''}`} onClick={() => pick(p)}>
              <PieceIcon icon={ALL_PIECES[p].icon} color="w" set={set.pieceSet} className="pg-chip-piece" />
              <span>{ALL_PIECES[p].name}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="pg-body" role="tabpanel" aria-label={def.name}>
        <div className="pg-board">
          <Board key={id} state={game} orientation="w" legal={legal} interactive={!game.result} onAction={onAction}
            lastAction={last?.action} events={last?.events} eventsKey={last?.key} marks={marks} />
        </div>
        <div className="pg-info">
          <h3>{def.name}</h3>
          <p>{pieceMoveText(def)}</p>
          <ul className="ability-list">
            {def.abilities.map((a) => (
              <li key={a.kind}>
                <b><Icon name={a.kind} size={17} /> {ABILITY_INFO[a.kind].name}</b>
                {a.cooldown > 0 && <span className="tag">cooldown {a.cooldown}</span>}
                <div className="muted small">{ABILITY_INFO[a.kind].desc}</div>
              </li>
            ))}
          </ul>
          <p className="pg-status" aria-live="polite">
            {game.result ? <><b>You took the king.</b> Reset to play again.</>
              : !game.board.some((p) => p?.type === id) ? <><b>Your {def.name} is gone.</b> Reset the board to try it again.</>
              : tried === 0 ? <>Tap the {def.name}, then a dot to move or an ability under the board.</>
              : <>{tried} {tried === 1 ? 'action' : 'actions'} tried. Black never moves back, so go wild.</>}
          </p>
          <div className="pg-cta">
            <button type="button" className="btn primary" onClick={play}>Play {preset.name} vs a bot <Icon name="arrow" size={16} /></button>
            <button type="button" className="btn ghost" onClick={reset}>Reset board</button>
          </div>
        </div>
      </div>
    </section>
  );
}
